import React from 'react';
import { ethers } from 'ethers';
import { fmt, tierLabel, shipImg, LEGENDARY_NAMES } from '../lib';

// Compact ORE: exact with commas below 100K (1,931), else 1.01M / 252K (exact value in the title tooltip).
export function compactOre(wei) {
  const n = Number(ethers.formatEther(wei ?? 0n));
  const a = Math.abs(n);
  const f = (v, u) => `${v >= 100 ? v.toFixed(0) : v >= 10 ? v.toFixed(1) : v.toFixed(2)}${u}`;
  if (a >= 1e9) return f(n / 1e9, 'B');
  if (a >= 1e6) return f(n / 1e6, 'M');
  if (a >= 1e5) return f(n / 1e3, 'K');
  return Math.floor(n).toLocaleString();
}

// One ship tile on the Fleet page. Pure layout: the page passes in the status text, the main action, the secondary text actions and the condition tooltip.
export default function ShipCard({ sh, status, sale, extras = [], action, condInfo }) {
  const pirate = sh.kind === 'Pirate';
  const tier = String(sh.tier).toLowerCase();
  const legend = sh.serial ? LEGENDARY_NAMES[sh.serial - 1] : null;
  const cond = Number(sh.durability);
  const condCls = cond <= 3 ? ' sc-bad' : cond <= 5 ? ' sc-warn' : '';
  const staked = sh.where === 'staked';
  return (
    <div className={`ship-card sc${pirate ? ' pirate' : ''}${Number(sh.gen) === 0 ? ' sc-g0' : ''}`} data-testid={`ship-${sh.id}`} style={{ position: 'relative' }}>
      <img src={shipImg(sh)} alt={tierLabel(sh)} title={tierLabel(sh)} className="sc-img" />
      <div className="sc-head">
        <span className="sc-id">#{String(sh.id)}</span>
        <span className={`sc-chip tier-${tier}`}>{sh.tier}</span>
        <span className={`sc-chip sc-gen sc-gen${Number(sh.gen)}`} title={Number(sh.gen) === 0 ? 'Genesis ship' : 'Built in the Shipyard'} data-testid="gen-badge">Gen{String(sh.gen)}</span>
      </div>
      {legend && <p className="sc-name tier-legendary" title={tierLabel(sh)}>#{String(sh.serial).padStart(2, '0')} {legend}</p>}
      <p className="sc-meta">{sh.kind}{pirate ? '' : ` · Lv ${sh.level}`}</p>
      <div className="sc-stats">
        <div>
          <p className="sp-label">{pirate ? 'Rank' : 'Power'}</p>
          <p className="sc-num">{pirate ? sh.rank : sh.power}</p>
        </div>
        <div>
          <p className="sp-label sc-lbl-i">Condition{condInfo}</p>
          <p className={`sc-num${condCls}`}>{cond}<span className="sp-unit">/10</span></p>
          <div className={`sc-bar${condCls}`} aria-hidden="true">{Array.from({ length: 10 }, (_, i) => <i key={i} className={i < cond ? 'on' : ''} />)}</div>
        </div>
      </div>
      <div className="sc-status">
        <p className="sp-label">{status}</p>
        {staked && <p className="sc-num sc-pend" title={`+${fmt(sh.pending, 0)} ORE`}>+{compactOre(sh.pending)}<span className="sp-unit"> ORE</span></p>}
        {sale && <p className="sc-num sc-pend">{sale}</p>}
      </div>
      {action}
      {extras.length > 0 && (
        <div className="sc-x" data-testid={`extras-${sh.id}`}>
          {extras.map((x, i) => (
            <React.Fragment key={x.label}>
              {i > 0 && <span className="sc-x-dot" aria-hidden="true">·</span>}
              <button type="button" className={`sc-x-btn ${x.cls || ''}`} disabled={x.disabled || x.busy} title={x.title} onClick={x.onClick}>{x.label}</button>
            </React.Fragment>
          ))}
        </div>
      )}
    </div>
  );
}
